/**
 * Capture configuration storage
 * Fetches from API using effective terminal API URL, then localStorage, then defaults
 */

import type { CaptureConfig } from '@photobooth/types';
import { DEFAULT_CAPTURE_CONFIG } from '@photobooth/types';
import { getEffectiveApiBaseUrl } from './terminal-urls';

const STORAGE_KEY = 'capture_config';

function mergeWithDefaults(partial: Partial<CaptureConfig> | null): CaptureConfig {
  if (!partial || typeof partial !== 'object') {
    return DEFAULT_CAPTURE_CONFIG;
  }
  return {
    countdownSeconds: partial.countdownSeconds ?? DEFAULT_CAPTURE_CONFIG.countdownSeconds,
    reviewPhotoSeconds: partial.reviewPhotoSeconds ?? DEFAULT_CAPTURE_CONFIG.reviewPhotoSeconds,
    editCountdownSeconds: partial.editCountdownSeconds ?? DEFAULT_CAPTURE_CONFIG.editCountdownSeconds,
  };
}

export async function getCaptureConfig(): Promise<CaptureConfig> {
  const baseUrl = getEffectiveApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/config/capture`);
    if (res.ok) {
      const data = (await res.json()) as Partial<CaptureConfig>;
      const config = mergeWithDefaults(data);
      setCaptureConfig(config);
      return config;
    }
  } catch {
    // Fall through to localStorage
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return mergeWithDefaults(stored ? (JSON.parse(stored) as Partial<CaptureConfig>) : null);
  } catch {
    return DEFAULT_CAPTURE_CONFIG;
  }
}

export function setCaptureConfig(config: CaptureConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}
